import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/lib/api';

export type Block = {
  id: string;
  projectId: string;
  code: string;
  name: string;
  position: number;
  floorPlanImageUrl: string | null;
  createdAt: string;
  _count?: { floors: number; apartments: number };
};

export function useBlocksForProject(projectId: string | undefined) {
  return useQuery({
    queryKey: ['blocks', projectId],
    enabled: !!projectId,
    queryFn: async () => {
      const res = await api.get<{ data: Block[] }>(`/projects/${projectId}/blocks`);
      return res.data.data;
    },
  });
}

export function useCreateBlock(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { code: string; name: string; position?: number }) => {
      const res = await api.post<{ data: Block }>(`/projects/${projectId}/blocks`, input);
      return res.data.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['blocks', projectId] });
      void qc.invalidateQueries({ queryKey: ['project', projectId] });
      void qc.invalidateQueries({ queryKey: ['apartments-grid', projectId] });
    },
  });
}

export function useDeleteBlock(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (blockId: string) => {
      await api.delete(`/blocks/${blockId}`);
      return blockId;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['blocks', projectId] });
      void qc.invalidateQueries({ queryKey: ['project', projectId] });
      void qc.invalidateQueries({ queryKey: ['apartments-grid', projectId] });
    },
  });
}
